import CircleCheckIcon from '@root/core/src/assets/icons/circle-check-icon';
import Coverage from '@root/auto-pricing/src/models/coverage';
import CoverageSelectionCard from '@root/quotes/src/components/coverage-options/coverage-selection-card';
import ExtraPipNjCard from '@root/quotes/src/components/coverage/extra-pip-nj-card';
import Money from '@root/core/src/models/money';
import NjPipMedicalExpensesLearnMoreModal from '@root/quotes/src/components/modals/nj-pip-medical-expenses-learn-more-modal';
import NjPipPrimaryInsurerLearnMoreModal from '@root/quotes/src/components/modals/nj-pip-primary-insurer-learn-more-modal';
import PropTypes from '@root/vendor/prop-types';
import QuotesMobileSelect from '@root/quotes/src/components/form-elements/quotes-mobile-select';
import QuotesSelect from '@root/quotes/src/components/form-elements/quotes-select';
import React from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import Select from '@root/core/src/components/select';
import { Colors, StyleSheet, Theme } from '@root/core/src/utils/styles';
import { ListCardItem } from '@root/core/src/components/list-card';
import { pipPeerMatches } from '@root/quotes/src/utils/pip-nj-filtering';

const SYMBOL = Coverage.Symbols.PERSONAL_INJURY_PROTECTION;

function getUniqueOptions(coverageSelections, coverage, dimension) {
  const seen = new Set();

  return coverageSelections.pip.filter((selection) => {
    const value = selection[dimension];
    if (seen.has(value)) { return false; }
    if (!pipPeerMatches(selection, coverage, [dimension])) { return false; }
    seen.add(value);
    return true;
  }).sort((a, b) => a[dimension] - b[dimension]).map((selection) => ({
    label: `${Money.fromDollars(selection[dimension]).formattedDollars()} ${dimension === 'perPerson' ? 'limit' : 'deductible'}`,
    subLabel: selection.value === coverage.id ? 'Selected' : selection.subLabel,
    value: selection.value,
  }));
}

function getPrimaryInsurerOptions(coverageSelections, coverage) {
  const seen = new Set();

  return coverageSelections.pip.filter((selection) => {
    const enhanced = selection.coverageOptions.enhanced;
    if (seen.has(enhanced)) { return false; }
    if (!pipPeerMatches(selection, coverage, ['enhanced'])) { return false; }
    seen.add(enhanced);
    return true;
  }).sort((a, b) => {
    // Root (enhanced=N) must lead the list (QA / design). Per the server contract, enhanced='N' is
    // "Root" and 'Y' is the insured's own health insurance.
    const toNum = (s) => s.coverageOptions.enhanced === Coverage.CoverageOptionChoice.NO ? 1 : 0;
    return toNum(b) - toNum(a);
  }).map((selection) => ({
    label: selection.coverageOptions.enhanced === Coverage.CoverageOptionChoice.YES ? 'My own health insurance' : 'Root',
    subLabel: selection.value === coverage.id ? 'Selected' : selection.subLabel,
    value: selection.value,
  }));
}

export default function PipNjCards({
  cardHeaderBackgroundColor,
  coverage,
  coverageSelections,
  dropdownIndicator,
  extMedCoverage,
  infoBrandColor,
  mobileSelectedOptionStyles,
  onCoverageChange,
  onCoverageSelectClose,
  onCoverageSelectOpen,
  optionStyles,
  primaryBrandColor,
  secondaryBrandColor,
  selectBoxFocusedStyles,
  selectBoxStyles,
}) {
  const limitOptions = getUniqueOptions(coverageSelections, coverage, 'perPerson');
  const deductibleOptions = getUniqueOptions(coverageSelections, coverage, 'deductible');
  const primaryInsurerOptions = getPrimaryInsurerOptions(coverageSelections, coverage);

  const selectedPrimaryInsurerValue = primaryInsurerOptions.find((o) => {
    const sel = coverageSelections.pip.find((s) => s.value === o.value);
    return sel && sel.coverageOptions.enhanced === coverage.coverageOptions.enhanced;
  })?.value ?? coverage.id;

  const selectedLimitValue = limitOptions.find((o) => {
    const sel = coverageSelections.pip.find((s) => s.value === o.value);
    return sel && sel.perPerson === coverage.perPerson;
  })?.value ?? coverage.id;

  const selectedDeductibleValue = deductibleOptions.find((o) => {
    const sel = coverageSelections.pip.find((s) => s.value === o.value);
    return sel && sel.deductible === coverage.deductible;
  })?.value ?? coverage.id;

  return (
    <>
      <CoverageSelectionCard
        description={'Covers medical bills if you or your passengers are injured in a car accident, no matter who caused it.'}
        headerBackgroundColor={cardHeaderBackgroundColor}
        heading={'Medical Expenses'}
        isCoverageAdded={true}
        renderLearnMore={() => (
          <NjPipMedicalExpensesLearnMoreModal
            infoBrandColor={infoBrandColor}
            primaryBrandColor={primaryBrandColor}
          />
        )}
        vehiclesSelected={true}
      >
        <ListCardItem css={styles.listItemBottom}>
          <div css={styles.desktopWrapper}>
            <Select
              dropdownIndicator={dropdownIndicator}
              dropdownIndicatorColor={secondaryBrandColor}
              inputId={'pip-limit-coverage-select'}
              inputName={'pip-limit-coverage-select'}
              onChange={(newValue) => onCoverageChange(newValue, SYMBOL)}
              onClose={onCoverageSelectClose}
              onOpen={onCoverageSelectOpen}
              options={limitOptions}
              optionStyles={optionStyles}
              placeholder={'Select PIP Limit'}
              selectBoxFocusedStyles={selectBoxFocusedStyles}
              selectBoxStyles={styles.selectBox}
              selectedValue={selectedLimitValue}
            />
            <Select
              dropdownIndicator={dropdownIndicator}
              dropdownIndicatorColor={secondaryBrandColor}
              inputId={'pip-deductible-coverage-select'}
              inputName={'pip-deductible-coverage-select'}
              onChange={(newValue) => onCoverageChange(newValue, SYMBOL)}
              onClose={onCoverageSelectClose}
              onOpen={onCoverageSelectOpen}
              options={deductibleOptions}
              optionStyles={optionStyles}
              placeholder={'Select PIP Deductible'}
              selectBoxFocusedStyles={selectBoxFocusedStyles}
              selectBoxStyles={styles.selectBox}
              selectedValue={selectedDeductibleValue}
            />
          </div>
          <div css={styles.mobileWrapper}>
            <QuotesMobileSelect
              dropdownIndicator={dropdownIndicator}
              dropdownIndicatorColor={secondaryBrandColor}
              inputId={'mobile-pip-limit-coverage-select'}
              inputName={'mobile-pip-limit-coverage-select'}
              mobileSelectedOptionStyles={mobileSelectedOptionStyles}
              onChange={(newValue) => onCoverageChange(newValue, SYMBOL)}
              onClose={onCoverageSelectClose}
              onOpen={onCoverageSelectOpen}
              options={limitOptions}
              optionStyles={optionStyles}
              placeholder={'Select PIP Limit'}
              selectBoxFocusedStyles={selectBoxFocusedStyles}
              selectBoxStyles={styles.selectBox}
              selectedValue={selectedLimitValue}
            />
            <QuotesMobileSelect
              dropdownIndicator={dropdownIndicator}
              dropdownIndicatorColor={secondaryBrandColor}
              inputId={'mobile-pip-deductible-coverage-select'}
              inputName={'mobile-pip-deductible-coverage-select'}
              mobileSelectedOptionStyles={mobileSelectedOptionStyles}
              onChange={(newValue) => onCoverageChange(newValue, SYMBOL)}
              onClose={onCoverageSelectClose}
              onOpen={onCoverageSelectOpen}
              options={deductibleOptions}
              optionStyles={optionStyles}
              placeholder={'Select PIP Deductible'}
              selectBoxFocusedStyles={selectBoxFocusedStyles}
              selectBoxStyles={styles.selectBox}
              selectedValue={selectedDeductibleValue}
            />
          </div>
        </ListCardItem>
      </CoverageSelectionCard>
      <ExtraPipNjCard
        cardHeaderBackgroundColor={cardHeaderBackgroundColor}
        coverage={coverage}
        coverageSelections={coverageSelections}
        dropdownIndicator={dropdownIndicator}
        infoBrandColor={infoBrandColor}
        mobileSelectedOptionStyles={mobileSelectedOptionStyles}
        onCoverageChange={onCoverageChange}
        onCoverageSelectClose={onCoverageSelectClose}
        onCoverageSelectOpen={onCoverageSelectOpen}
        optionStyles={optionStyles}
        primaryBrandColor={primaryBrandColor}
        secondaryBrandColor={secondaryBrandColor}
        selectBoxFocusedStyles={selectBoxFocusedStyles}
        selectBoxStyles={selectBoxStyles}
      />
      <CoverageSelectionCard
        description={
          'Please select whether Root or your health insurance will be the primary insurer for medical expenses.' +
          ' An additional deductible will apply if you select a health insurer as primary insurer and they do not' +
          ' cover medical expenses as a result of a motor vehicle accident.'
        }
        headerBackgroundColor={cardHeaderBackgroundColor}
        heading={'Primary Insurer'}
        isCoverageAdded={true}
        renderLearnMore={() => (
          <NjPipPrimaryInsurerLearnMoreModal
            infoBrandColor={infoBrandColor}
            primaryBrandColor={primaryBrandColor}
          />
        )}
        vehiclesSelected={true}
      >
        <QuotesSelect
          dropdownIndicator={dropdownIndicator}
          dropdownIndicatorColor={secondaryBrandColor}
          inputId={'pip-primary-insurer-coverage-select'}
          inputName={'pip-primary-insurer-coverage-select'}
          mobileSelectedOptionStyles={mobileSelectedOptionStyles}
          onChange={(newValue) => onCoverageChange(newValue, SYMBOL)}
          onClose={onCoverageSelectClose}
          onOpen={onCoverageSelectOpen}
          options={primaryInsurerOptions}
          optionStyles={optionStyles}
          placeholder={'Select option'}
          selectBoxFocusedStyles={selectBoxFocusedStyles}
          selectBoxStyles={selectBoxStyles}
          selectedValue={selectedPrimaryInsurerValue}
        />
      </CoverageSelectionCard>
      {extMedCoverage && (
        <CoverageSelectionCard
          description={'Covers losses and expenses incurred because of bodily injury you sustained from a covered accident in a highway vehicle.'}
          headerBackgroundColor={cardHeaderBackgroundColor}
          heading={'Extended Medical Expense'}
          isCoverageAdded={true}
          onRemoveCoverage={null}
          vehiclesSelected={true}
        >
          <ListCardItem css={njExtMedStyles.body}>
            <div css={njExtMedStyles.titleRow}>
              <div css={njExtMedStyles.iconWrapper}>
                <CircleCheckIcon fillColor={Colors.success()} />
              </div>
              <span css={njExtMedStyles.title}>{`Coverage included: ${Money.fromDollars(extMedCoverage.perPerson).formattedDollars()}`}</span>
            </div>
            <p css={njExtMedStyles.subtext}>{'This coverage is required for your state.'}</p>
          </ListCardItem>
        </CoverageSelectionCard>
      )}
    </>
  );
}

const njExtMedStyles = StyleSheet.create({
  body: {
    background: Colors.nearWhite(),
    border: `1px solid ${Colors.gray30()}`,
    borderRadius: '0 0 10px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 24,
  },
  titleRow: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: 10,
  },
  iconWrapper: {
    flexShrink: 0,
    '& svg': {
      display: 'block',
      height: 28,
      width: 28,
    },
  },
  title: {
    ...Theme.heading4(),
  },
  subtext: {
    margin: 0,
    ...Theme.paragraph2(),
  },
});

const styles = StyleSheet.create({
  listItemBottom: {
    borderRadius: '0 0 10px 10px',
    border: `1px solid ${Colors.gray30()}`,
    background: Colors.nearWhite(),
  },
  selectBox: {
    color: Colors.black(),
  },
  mobileWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    ...Responsive.md({
      display: 'none',
    }),
  },
  desktopWrapper: {
    display: 'none',
    ...Responsive.md({
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }),
  },
});

PipNjCards.propTypes = {
  cardHeaderBackgroundColor: PropTypes.string,
  coverage: PropTypes.object.isRequired,
  coverageSelections: PropTypes.object.isRequired,
  dropdownIndicator: PropTypes.string,
  extMedCoverage: PropTypes.object,
  infoBrandColor: PropTypes.string,
  mobileSelectedOptionStyles: PropTypes.object,
  onCoverageChange: PropTypes.func.isRequired,
  onCoverageSelectClose: PropTypes.func.isRequired,
  onCoverageSelectOpen: PropTypes.func.isRequired,
  optionStyles: PropTypes.object,
  primaryBrandColor: PropTypes.string,
  secondaryBrandColor: PropTypes.string,
  selectBoxFocusedStyles: PropTypes.object,
  selectBoxStyles: PropTypes.object,
};
